import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  TrendingUp,
  Map,
  ClipboardList,
  Users,
  Sparkles,
  Plus,
  ChevronRight,
  CheckCircle,
  Circle,
  Clock,
  XCircle,
  Star,
  ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const TABS = [
  { id: "tracks",      icon: Map,           section: "reference" },
  { id: "myplan",      icon: ClipboardList, section: "myCareer" },
  { id: "suggestions", icon: Sparkles,      section: "myCareer" },
  { id: "team",        icon: Users,         section: "manager" },
];

const STATUS_CONFIG = {
  draft:     { color: "bg-gray-100 text-gray-700",   icon: Circle },
  active:    { color: "bg-blue-100 text-blue-700",   icon: Clock },
  completed: { color: "bg-green-100 text-green-700", icon: CheckCircle },
  cancelled: { color: "bg-red-100 text-red-700",     icon: XCircle },
};

const GOAL_STATUS = {
  pending:     { color: "text-muted-foreground", icon: Circle },
  in_progress: { color: "text-blue-600",         icon: Clock },
  completed:   { color: "text-green-600",        icon: CheckCircle },
  cancelled:   { color: "text-red-500",          icon: XCircle },
};

const statusLabel = (status, t) => t(`careerHub.status.${status}`, { defaultValue: status });
const goalCategoryLabel = (category, t) => t(`careerHub.goalCategories.${category}`, { defaultValue: category });

export default function CareerHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "tracks";
  const setTab = (tab) => setSearchParams({ tab });
  const sections = [...new Set(TABS.map((t) => t.section))];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingUp className="h-6 w-6 text-indigo-500" />
          {t("careerHub.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("careerHub.subtitle")}
        </p>
      </div>

      <div className="border-b">
        {sections.map((section) => (
          <div key={section} className="inline-block mr-6 mb-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-3">
              {t(`careerHub.sections.${section}`)}
            </span>
            {TABS.filter((t) => t.section === section).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors mr-1 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(`careerHub.tabs.${tab.id}`)}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div>
        {activeTab === "tracks"      && <TracksTab />}
        {activeTab === "myplan"      && <MyPlanTab />}
        {activeTab === "suggestions" && <SuggestionsTab />}
        {activeTab === "team"        && <TeamTab />}
      </div>
    </div>
  );
}

// ── Tracks ────────────────────────────────────────────────────────────────────

function TracksTab() {
  const { t } = useTranslation();
  const [tracks, setTracks] = useState([]);
  const [stats, setStats] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [tr, st] = await Promise.all([
      apiClient.get("v1/career/tracks").catch(() => ({ data: [] })),
      apiClient.get("v1/career/stats").catch(() => ({ data: null })),
    ]);
    setTracks(tr.data || []);
    setStats(st.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTrack = async () => {
    if (!form.name.trim()) { toast.error(t("careerHub.tracks.nameRequired")); return; }
    setSaving(true);
    try {
      await apiClient.post("v1/career/tracks", { ...form, levels: [] });
      toast.success(t("careerHub.tracks.created"));
      setShowForm(false);
      setForm({ name: "", description: "" });
      load();
    } catch { toast.error(t("careerHub.tracks.createError")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label={t("careerHub.tracks.statTracks")} value={stats.total_tracks} />
          <MiniStat label={t("careerHub.tracks.statActivePlans")} value={stats.active_plans} />
          <MiniStat label={t("careerHub.tracks.statTotalPlans")} value={stats.total_plans} />
          <MiniStat label={t("careerHub.tracks.statCompletedGoals")} value={stats.completed_goals} />
        </div>
      )}

      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{t("careerHub.tracks.count", { count: tracks.length })}</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-1" /> {t("careerHub.tracks.new")}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardContent className="pt-4 space-y-3">
            <Input
              placeholder={t("careerHub.tracks.namePlaceholder")}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <Textarea
              placeholder={t("careerHub.tracks.descriptionPlaceholder")}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={createTrack} disabled={saving}>{saving ? t("careerHub.tracks.creating") : t("careerHub.tracks.createCta")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>{t("careerHub.common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {tracks.map((track) => (
          <Card key={track.id}>
            <CardHeader className="pb-2 pt-4 px-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">{track.name}</CardTitle>
                  {track.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">{track.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{t("careerHub.tracks.levelCount", { count: track.levels?.length || 0 })}</Badge>
                  <button
                    onClick={() => setExpanded(expanded === track.id ? null : track.id)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ChevronRight className={`h-4 w-4 transition-transform ${expanded === track.id ? "rotate-90" : ""}`} />
                  </button>
                </div>
              </div>
            </CardHeader>

            {expanded === track.id && track.levels?.length > 0 && (
              <CardContent className="pt-0 pb-4 px-4">
                <div className="flex items-stretch gap-0 mt-2 overflow-x-auto pb-1">
                  {track.levels.map((level, idx) => (
                    <React.Fragment key={level.id}>
                      <div className="flex-shrink-0 w-40 bg-muted/40 rounded-lg p-3 text-center">
                        <div className="text-xs font-semibold text-muted-foreground mb-1">
                          {t("careerHub.tracks.levelNumber", { number: level.level_number })}
                        </div>
                        <p className="font-semibold text-sm">{level.title}</p>
                        {level.description && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{level.description}</p>
                        )}
                        {(level.min_salary || level.max_salary) && (
                          <p className="text-xs text-primary font-medium mt-1">
                            {level.min_salary && `${Math.round(level.min_salary / 1000)}k`}
                            {level.min_salary && level.max_salary && " – "}
                            {level.max_salary && `${Math.round(level.max_salary / 1000)}k`}
                          </p>
                        )}
                        {level.skill_requirements?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1 justify-center">
                            {level.skill_requirements.slice(0, 3).map((sr) => (
                              <span key={sr.id} className="text-xs bg-background border rounded px-1">
                                {sr.skill?.name}
                              </span>
                            ))}
                            {level.skill_requirements.length > 3 && (
                              <span className="text-xs text-muted-foreground">+{level.skill_requirements.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                      {idx < track.levels.length - 1 && (
                        <div className="flex items-center flex-shrink-0 px-1 text-muted-foreground">
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── My Plan ───────────────────────────────────────────────────────────────────

function MyPlanTab() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [showNewPlan, setShowNewPlan] = useState(false);
  const [showNewGoal, setShowNewGoal] = useState(false);
  const [planForm, setPlanForm] = useState({ title: "", notes: "", target_level_id: "none" });
  const [goalForm, setGoalForm] = useState({ title: "", category: "skill", due_date: "", progress_pct: 0 });
  const [saving, setSaving] = useState(false);

  const GOAL_CATEGORIES = ["skill", "behavior", "experience", "certification", "other"];

  const load = useCallback(async () => {
    const [plRes, trRes] = await Promise.all([
      apiClient.get("v1/career/plans/me").catch(() => ({ data: [] })),
      apiClient.get("v1/career/tracks").catch(() => ({ data: [] })),
    ]);
    setPlans(plRes.data || []);
    setTracks(trRes.data || []);
    if (plRes.data?.length && !activePlan) {
      const active = plRes.data.find((p) => p.status === "active") || plRes.data[0];
      setActivePlan(active);
    }
  }, [activePlan]);

  useEffect(() => { load(); }, [load]);

  const createPlan = async () => {
    if (!planForm.title.trim()) { toast.error(t("careerHub.myPlan.titleRequired")); return; }
    setSaving(true);
    try {
      const res = await apiClient.post("v1/career/plans", {
        ...planForm,
        status: "active",
        target_level_id: planForm.target_level_id && planForm.target_level_id !== "none" ? parseInt(planForm.target_level_id) : null,
      });
      toast.success(t("careerHub.myPlan.planCreated"));
      setShowNewPlan(false);
      setPlanForm({ title: "", notes: "", target_level_id: "none" });
      await load();
      setActivePlan(res.data);
    } catch { toast.error(t("careerHub.myPlan.planCreateError")); }
    finally { setSaving(false); }
  };

  const addGoal = async () => {
    if (!goalForm.title.trim() || !activePlan) return;
    setSaving(true);
    try {
      await apiClient.post(`v1/career/plans/${activePlan.id}/goals`, {
        ...goalForm,
        progress_pct: parseInt(goalForm.progress_pct) || 0,
        due_date: goalForm.due_date || null,
      });
      toast.success(t("careerHub.myPlan.goalAdded"));
      setShowNewGoal(false);
      setGoalForm({ title: "", category: "skill", due_date: "", progress_pct: 0 });
      await load();
    } catch { toast.error(t("careerHub.myPlan.goalAddError")); }
    finally { setSaving(false); }
  };

  const updateGoalProgress = async (goalId, progress) => {
    try {
      await apiClient.patch(`v1/career/goals/${goalId}`, {
        progress_pct: progress,
        status: progress >= 100 ? "completed" : progress > 0 ? "in_progress" : "pending",
      });
      load();
    } catch { toast.error(t("careerHub.myPlan.updateError")); }
  };

  // Flatten levels for select
  const allLevels = tracks.flatMap((t) =>
    (t.levels || []).map((l) => ({ ...l, track_name: t.name }))
  );

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("careerHub.myPlan.count", { count: plans.length })}</p>
        <Button size="sm" onClick={() => setShowNewPlan(!showNewPlan)}>
          <Plus className="h-4 w-4 mr-1" /> {t("careerHub.myPlan.newPlan")}
        </Button>
      </div>

      {showNewPlan && (
        <Card className="border-primary/30">
          <CardHeader><CardTitle className="text-sm">{t("careerHub.myPlan.createTitle")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder={t("careerHub.myPlan.titlePlaceholder")} value={planForm.title} onChange={(e) => setPlanForm((f) => ({ ...f, title: e.target.value }))} />
            <div>
              <label className="text-xs font-medium mb-1 block">{t("careerHub.myPlan.targetLevel")}</label>
              <Select value={planForm.target_level_id} onValueChange={(v) => setPlanForm((f) => ({ ...f, target_level_id: v }))}>
                <SelectTrigger><SelectValue placeholder={t("careerHub.myPlan.noTargetLevel")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("careerHub.myPlan.none")}</SelectItem>
                  {allLevels.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>
                      {l.track_name} — {l.title} ({t("careerHub.myPlan.levelShort", { number: l.level_number })})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea placeholder={t("careerHub.myPlan.notesPlaceholder")} value={planForm.notes} onChange={(e) => setPlanForm((f) => ({ ...f, notes: e.target.value }))} rows={2} />
            <div className="flex gap-2">
              <Button size="sm" onClick={createPlan} disabled={saving}>{saving ? t("careerHub.myPlan.creating") : t("careerHub.myPlan.createCta")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowNewPlan(false)}>{t("careerHub.common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan selector */}
      {plans.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {plans.map((p) => {
            const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.draft;
            return (
              <button
                key={p.id}
                onClick={() => setActivePlan(p)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${activePlan?.id === p.id ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
              >
                {p.title}
                <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${cfg.color}`}>{statusLabel(p.status, t)}</span>
              </button>
            );
          })}
        </div>
      )}

      {plans.length === 0 && !showNewPlan && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("careerHub.myPlan.empty")}</p>
            <p className="text-sm">{t("careerHub.myPlan.emptyHint")}</p>
          </CardContent>
        </Card>
      )}

      {activePlan && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-base">{activePlan.title}</CardTitle>
                {activePlan.target_level && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("careerHub.myPlan.target")} {activePlan.target_level.track?.name} → {activePlan.target_level.title}
                  </p>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => setShowNewGoal(!showNewGoal)}>
                <Plus className="h-3 w-3 mr-1" /> {t("careerHub.myPlan.goal")}
              </Button>
            </div>
            {activePlan.notes && (
              <p className="text-sm text-muted-foreground mt-1">{activePlan.notes}</p>
            )}

            {/* Overall progress */}
            {activePlan.goals?.length > 0 && (() => {
              const avg = Math.round(activePlan.goals.reduce((s, g) => s + g.progress_pct, 0) / activePlan.goals.length);
              return (
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">{t("careerHub.myPlan.overallProgress")}</span>
                    <span className="font-medium">{avg}%</span>
                  </div>
                  <Progress value={avg} className="h-1.5" />
                </div>
              );
            })()}
          </CardHeader>

          <CardContent className="pt-2 space-y-2">
            {showNewGoal && (
              <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                <Input placeholder={t("careerHub.myPlan.goalTitlePlaceholder")} value={goalForm.title} onChange={(e) => setGoalForm((f) => ({ ...f, title: e.target.value }))} />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={goalForm.category} onValueChange={(v) => setGoalForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GOAL_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{goalCategoryLabel(c, t)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={goalForm.due_date} onChange={(e) => setGoalForm((f) => ({ ...f, due_date: e.target.value }))} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={addGoal} disabled={saving}>{saving ? t("careerHub.myPlan.adding") : t("careerHub.myPlan.addCta")}</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowNewGoal(false)}>{t("careerHub.common.cancel")}</Button>
                </div>
              </div>
            )}

            {(activePlan.goals || []).length === 0 && !showNewGoal && (
              <p className="text-sm text-muted-foreground py-3 text-center">{t("careerHub.myPlan.noGoals")}</p>
            )}

            {(activePlan.goals || []).map((goal) => {
              const cfg = GOAL_STATUS[goal.status] || GOAL_STATUS.pending;
              const Icon = cfg.icon;
              return (
                <div key={goal.id} className="border rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${cfg.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{goal.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="outline" className="text-xs">{goalCategoryLabel(goal.category, t)}</Badge>
                        {goal.due_date && (
                          <span className="text-xs text-muted-foreground">
                            {t("careerHub.myPlan.dueDate", { date: new Date(goal.due_date).toLocaleDateString() })}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Progress value={goal.progress_pct} className="h-1.5 flex-1" />
                        <span className="text-xs font-medium w-8 text-right">{goal.progress_pct}%</span>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={10}
                          value={goal.progress_pct}
                          onChange={(e) => updateGoalProgress(goal.id, parseInt(e.target.value))}
                          className="w-20 h-1 accent-primary cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Suggestions ───────────────────────────────────────────────────────────────

function SuggestionsTab() {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get("v1/career/suggestions/me/next")
      .then((r) => setSuggestions(r.data || []))
      .catch(() => toast.error(t("careerHub.suggestions.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) return <p className="text-muted-foreground">{t("careerHub.suggestions.analyzing")}</p>;

  if (suggestions.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p>{t("careerHub.suggestions.empty")}</p>
          <p className="text-sm">{t("careerHub.suggestions.emptyHint")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-muted-foreground">
        {t("careerHub.suggestions.basedOnSkills")}
      </p>
      {suggestions.map((s, idx) => (
        <Card key={s.level_id} className={idx === 0 ? "border-primary/30 ring-1 ring-primary/20" : ""}>
          <CardContent className="py-4 px-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {idx === 0 && <Star className="h-4 w-4 text-yellow-500" />}
                  <p className="font-semibold">
                    {s.track_name} — {s.level_title}
                    <span className="text-sm font-normal text-muted-foreground ml-2">{t("careerHub.myPlan.levelShort", { number: s.level_number })}</span>
                  </p>
                </div>
                {(s.min_salary || s.max_salary) && (
                  <p className="text-xs text-primary font-medium mt-0.5">
                    {s.min_salary && `${Math.round(s.min_salary / 1000)}k`}
                    {s.min_salary && s.max_salary && " – "}
                    {s.max_salary && `${Math.round(s.max_salary / 1000)}k`}
                  </p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-2xl font-bold text-primary">{s.readiness_pct}%</p>
                <p className="text-xs text-muted-foreground">{t("careerHub.suggestions.ready")}</p>
              </div>
            </div>

            <div className="mt-3">
              <Progress value={s.readiness_pct} className="h-2" />
            </div>

            {s.gaps.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {t("careerHub.suggestions.gapsTitle", { count: s.gaps.length })}
                </p>
                <div className="space-y-1">
                  {s.gaps.slice(0, 4).map((g) => (
                    <div key={g.skill_id} className="flex items-center justify-between text-xs">
                      <span className="text-foreground">{g.skill_name}</span>
                      <span className="text-muted-foreground">
                        {g.current_label || t("careerHub.suggestions.notAssessed")} → {g.required_label}
                      </span>
                    </div>
                  ))}
                  {s.gaps.length > 4 && (
                    <p className="text-xs text-muted-foreground">{t("careerHub.suggestions.more", { count: s.gaps.length - 4 })}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Team ──────────────────────────────────────────────────────────────────────

function TeamTab() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get("v1/career/plans")
      .then((r) => setPlans(r.data || []))
      .catch(() => toast.error(t("careerHub.team.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  if (loading) return <p className="text-muted-foreground">{t("careerHub.loading")}</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t("careerHub.team.count", { count: plans.length })}</p>

      {plans.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("careerHub.team.empty")}</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {plans.map((plan) => {
          const cfg = STATUS_CONFIG[plan.status] || STATUS_CONFIG.draft;
          const goals = plan.goals || [];
          const completed = goals.filter((g) => g.status === "completed").length;
          const progress = goals.length ? Math.round(goals.reduce((s, g) => s + g.progress_pct, 0) / goals.length) : 0;

          return (
            <Card key={plan.id}>
              <CardContent className="py-3 px-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm">{plan.title}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${cfg.color}`}>{statusLabel(plan.status, t)}</span>
                    </div>
                    {plan.employee && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {plan.employee.first_name} {plan.employee.last_name}
                        {plan.target_level && ` → ${plan.target_level.title}`}
                      </p>
                    )}
                    {goals.length > 0 && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <Progress value={progress} className="h-1 flex-1 max-w-[120px]" />
                        <span className="text-xs text-muted-foreground">{t("careerHub.team.goalsProgress", { completed, total: goals.length })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function MiniStat({ label, value }) {
  return (
    <Card>
      <CardContent className="py-3 px-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold">{value ?? "—"}</p>
      </CardContent>
    </Card>
  );
}
